import * as React from 'react';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import {
  Chip,
  TableCell,
  TableRow,
  Popper,
  Box,
  Paper,
  Typography,
  TextField,
  Divider,
  Stack,
  FormHelperText,
  FormControl,
  TableBody
} from '@mui/material';
import InputError, { InputErrorProps } from './InputError';
import { useAutoSubmitSignal } from 'hooks/autosubmit';
import { useFieldInfo } from 'contexts/FieldInfoContext';
import SearchIcon from '@mui/icons-material/Search';
import { filterTableOptions, getColumns, isOption } from 'utils/option-utils';
import { isEmptyValue, useDebounce } from 'utils/utils';
import { validateField } from '../utils/validate-utils';
import { Option } from '../../../types/option';
import { useRef } from 'react';
import { useTranslator } from 'hooks/translator';
import { focusRef, Results } from './select/common';
import CancelIcon from '@mui/icons-material/Cancel';

function InputSingleEntityFieldLocalContent(props: InputErrorProps) {
  const { augProps, fieldProps, info } = useFieldInfo()

  const filterRef                                       = useRef<HTMLDivElement>(null);
  const [input, setInput, debouncedInput, skipDebounce] = useDebounce("", 500)
  const [selected, setSelected]                         = React.useState<null | Option>(null)
  const [highlighted, highlight]                        = React.useState<null | Option>(null)
  const [anchorEl, setAnchorEl]                         = React.useState<null | HTMLElement>(null);
  const [options, setOptions]                           = React.useState<Option[]>(info.options || []);
  const [loading, setLoading ]                          = React.useState(false)
  const { setFocus, setViewOpen }                       = props
  const { signal }                                      = useAutoSubmitSignal()
  const { t }                                           = useTranslator()
  const open = Boolean(anchorEl);
  
  function openPopper(elem: HTMLElement | null) {
    setAnchorEl(elem)
    setViewOpen(true)
    setFocus(true)
    focusRef(filterRef)
  }

  function closePopper() {
    setFocus(false)
    setViewOpen(false)
    setAnchorEl(null)
  }

  React.useEffect(() => {
      const value = fieldProps.value
      if (isEmptyValue(value))
        setSelected(null)
      else {
        const option = isOption(value)
          ? value
          : getOption(value)

        if (selected && (selected.value === value || selected.value === option?.value)) return

        setSelected(option)
        augProps.setValue(option)
        handleValidate(option)
      }
    }, [fieldProps.value]);

  React.useEffect(() => {
      if (!(open))
        return;
  
      setLoading(true)
  
      let active = true;
      (async () => {
        if (!info.options) return
        const options = info.options
        const filteredOptions = isEmptyValue(debouncedInput)
          ? options
          : filterTableOptions(options, debouncedInput)
  
        if (active) {
          setOptions(filteredOptions)
          setLoading(false)
        }
      })();
  
      return () => { active = false; }
    }, [open, debouncedInput]);

  React.useEffect(() => {
      if (!open) {
        setOptions([]);
        setLoading(false)
      } else {
        if (input !== debouncedInput)
          setLoading(true)
  
        skipDebounce()
      }
    }, [input, open])

  function getOption(value: string): Option | null {
    return options.find(opt => opt.value === value)
      || info.options?.find(opt => opt.value === value)
      || null
  }

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    openPopper(e.currentTarget.parentElement);
    e.stopPropagation()
  }
  const handleDelete = (e: React.SyntheticEvent) => {
    setSelected(null)
    augProps.setValue(null)
    handleValidate(null, e)
  }

  function handleBlur (e: any) {
    const id = fieldProps.id
    fieldProps.onBlur({id: id, target: document.getElementById(id), ...e})
    handleValidate(fieldProps.value, e)
    setFocus(false)
    closePopper()
    signal()
  }

  function handleFocus(e: React.SyntheticEvent) {
    setFocus(true)
    fieldProps.onFocus({id: fieldProps.id, ...e})
  }

  function handleValidate(option: Option | null, e?: React.SyntheticEvent) {
    const error = validateField("select", fieldProps.required, e || null, option)
    augProps.setError(error)
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    setInput(e.target.value)
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    const optionsExist = options.length > 0

    function select(option: Option) {
      e.stopPropagation()
      selectOption(option)
      fieldProps.onBlur(e)
    }

    function highlightChange(nextIdx: Function, defaultIdx: number) {
      e.preventDefault()
      if (!optionsExist) return
      if (!highlighted) {
        highlight(options[defaultIdx])
        e.stopPropagation()
        return
      }

      const curIdx = options.indexOf(highlighted)
      if (curIdx === -1) {
        highlight(options[defaultIdx])
        e.stopPropagation()
        return
      }

      highlight(options[nextIdx(curIdx)])
    }

    switch (e.key) {
      case "Enter":
        e.preventDefault()
        if (loading) {
          e.stopPropagation()
          return
        }
        if (highlighted && options.indexOf(highlighted) >= 0) {
          select(highlighted)
          return
        }
        if (optionsExist) {
          select(options[0])
          return
        }
        break
      case "Escape":
        e.preventDefault()
        handleValidate(fieldProps.value)
        closePopper()
        fieldProps.onBlur(e)
        break
      case "Tab":
      case "ArrowDown":
        highlightChange(
          (curIdx: number) => (curIdx + 1) % options.length,
          0
        )
        break
      case "ArrowUp":
        highlightChange(
          (curIdx: number) => curIdx === 0 ? options.length - 1 : (curIdx - 1) % options.length,
          options.length - 1
        )
        break
      default:
    }
  }

  function rowClickHandler(value: string) {
    return (e: React.SyntheticEvent) => {
      const option = getOption(value)
      if (!option) {
        console.error("Couldn't find selected option with value %s", value)
        return
      }
      selectOption(option)
    }
  }

  function selectOption(option: Option) {
    setSelected(option)
    augProps.setValue(option.value)
    handleValidate(option)
    closePopper()
  }

  const resultProps = {
    loading: loading,
    empty: options.length === 0,
    header: <TableRow>{getColumns(options).map(col => <TableCell key={col}>{col}</TableCell>)}</TableRow>,
    body: <TableBody>{options.map((option, idx) => 
        <TableRow
            key={option.value}
            onClick={rowClickHandler(option.value)}
            hover
            selected={option === highlighted}
            sx={{ cursor: 'pointer' }}>
          {Object.entries(option.tuple!).map(val => <TableCell key={[val[0], option.value, val[1]].join('_')}>{val[1]}</TableCell>)}
        </TableRow>)}</TableBody>
  }

  const error = augProps.error
  const selectedExists = selected !== null
  const extraProps = selectedExists ? { onDelete: handleDelete } : {}
  const textColor = error ? 'error' : null

  const selectedLabel = selectedExists
      ? <Typography display='inline' color={textColor || 'default'}>{selected.label}</Typography>
      : <Typography display='inline' color={textColor || '#6b778c'}>{t("select.nothingselected")}</Typography>
  const label = info.inMultiple
    ? selectedLabel
    : <Stack direction='row' spacing='16px'>
        <Typography color={textColor || '#6b778c'} display='inline'>
          {fieldProps.label + (fieldProps.required ? ' *' : '')}
        </Typography>
        <Divider orientation="vertical" flexItem />
        {selectedLabel}
      </Stack>

  // TODO: check if zIndex 1200 is ideal or should be lower
  return <>
    <FormControl margin={info.inMultiple ? "none" : "normal"}
                 error={Boolean(error)}
                 onFocus={(e) => openPopper(e.currentTarget) }
                 sx={{minWidth: "100%", width: 'max-content'}}>
      <Chip sx={{ minHeight: info.inMultiple ? '40px' : '56px', borderRadius: '5px', justifyContent: 'left' }}
            id={fieldProps.id}
            clickable
            variant='outlined'
            onClick={handleClick}
            deleteIcon={<CancelIcon onMouseDown={(e) => e.preventDefault()} /* prevent receiving focus when clicking delete button*/ />}
            label={label}
            color={error ? 'error' : 'default'}
            {...extraProps}
            />
      {info.inMultiple ? null : <FormHelperText>{error || ''}</FormHelperText>}
    </FormControl>
    <Popper
        open={open}
        anchorEl={anchorEl}
        placement="bottom-start"
        sx={{zIndex: 1200}}>
      <ClickAwayListener onClickAway={handleBlur}>
        <Paper elevation={4} sx={{padding: '5 px'}}>
          <Box sx={{ display: 'flex', alignItems: 'center', p: 1.5 }}>
            <SearchIcon sx={{ color: 'action.active', mr: 1, my: 0.5 }} />
            <TextField
                id='filter-options'
                variant='standard'
                hiddenLabel
                // onBlur={handleBlur}
                onFocus={handleFocus}
                value={input}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                inputRef={filterRef}/>
          </Box>
          <Divider/>
          <Box sx={{ p: 0 }}>
            <Results {...resultProps}/>
          </Box>
        </Paper>
      </ClickAwayListener>
    </Popper>
  </>
}

const InputSingleEntityFieldLocal = () => (
  <InputError>
    {/* @ts-ignore */}
    <InputSingleEntityFieldLocalContent />
  </InputError>
)

export default InputSingleEntityFieldLocal


